/* eslint-disable replo/no-tailwind-literal-color */
import type { FileWithContent } from "schemas/generated/file";
import type { Funnel } from "schemas/generated/funnel";
import type { FunnelStep } from "schemas/generated/funnelStep";

import React, { useState } from "react";

import Input from "@editor/components/common/designSystem/Input";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import useCurrentUser from "@editor/hooks/useCurrentUser";
import { trpc } from "@editor/utils/trpc";

import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Button from "@replo/design-system/components/button/Button";
import { Combobox } from "@replo/design-system/components/combobox/Combobox";
import Popover from "@replo/design-system/components/popover/Popover";
import { skipToken } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";
import { getRelativePathFromFilePath } from "schemas/utils/file";

function ManageFunnels() {
  const projectId = useCurrentProjectId();
  const { user } = useCurrentUser();
  const [newFunnelName, setNewFunnelName] = useState("");
  const [newPageName, setNewPageName] = useState("");
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const { data: pages, refetch: refetchPages } = trpc.file.list.useQuery(
    projectId ? { projectId, fileType: "page" } : skipToken,
  );

  const { mutate: createPage, isPending: createPageLoading } =
    trpc.file.createPage.useMutation({
      onSuccess: () => {
        void refetchPages();
      },
    });

  const { mutate: createFunnel, isPending: createFunnelLoading } =
    trpc.funnel.create.useMutation({
      onSuccess: () => {
        void refetchFunnels();
      },
    });

  const { mutate: initializeFiles, isPending: initializeFilesLoading } =
    trpc.file.initializeFiles.useMutation({
      onSuccess: () => {
        void refetchPages();
        void refetchFunnels();
      },
    });

  const { data: funnels, refetch: refetchFunnels } = trpc.funnel.list.useQuery(
    projectId ? { projectId } : skipToken,
  );

  return (
    <div className="flex gap-2">
      <Popover.Root isOpen={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
        <Popover.Trigger>
          <Button variant="secondary">Funnels & Pages</Button>
        </Popover.Trigger>
        <Popover.Content
          className="ml-7 w-full"
          side="bottom"
          title="Funnels & Pages"
          shouldPreventDefaultOnInteractOutside={false}
        >
          <div className="flex flex-col gap-2 mt-3">
            <div>
              <div>Initialize Files</div>
              <Button
                variant="secondary"
                isLoading={initializeFilesLoading}
                onClick={() => {
                  initializeFiles({
                    projectId: projectId ?? "",
                    userId: user?.id ?? "",
                  });
                  void refetchPages();
                  void refetchFunnels();
                }}
              >
                Initialize Files
              </Button>
            </div>
            <div className="text-lg font-medium">Funnels</div>
            <div className="flex gap-2">
              <Input
                placeholder="New Funnel Name"
                value={newFunnelName}
                onChange={(e) => setNewFunnelName(e.target.value)}
              />

              <Button
                isLoading={createFunnelLoading}
                variant="primary"
                onClick={() =>
                  void createFunnel({
                    projectId: projectId ?? "",
                    name: newFunnelName,
                  })
                }
              >
                Create
              </Button>
            </div>
            <div className="flex flex-wrap gap-3">
              {funnels?.map((funnel) => (
                <FunnelCard
                  key={funnel.id}
                  funnel={funnel}
                  projectId={projectId ?? ""}
                  refetchFunnels={() => {
                    void refetchFunnels();
                  }}
                />
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-2 mt-3">
            <div className="text-lg font-medium">Pages</div>
            <div className="flex gap-2">
              <Input
                placeholder="/new-page"
                value={newPageName}
                onChange={(e) => setNewPageName(e.target.value)}
              />

              <Button
                isLoading={createPageLoading}
                variant="primary"
                onClick={() =>
                  void createPage({
                    projectId: projectId ?? "",
                    path: newPageName,
                  })
                }
              >
                Create
              </Button>
            </div>
            <div className="flex flex-wrap gap-3">
              {pages?.map((page) => (
                <PageCard
                  key={page.path}
                  page={page}
                  projectId={projectId ?? ""}
                  refetchPages={() => {
                    void refetchPages();
                  }}
                />
              ))}
            </div>
          </div>
        </Popover.Content>
      </Popover.Root>
    </div>
  );
}

const orderFunnelSteps = (funnelSteps: FunnelStep[]) => {
  const firstStep = funnelSteps.find((step) => step.previousStepId === null);
  if (!firstStep) {
    return [];
  }

  const orderedSteps: FunnelStep[] = [firstStep];
  let currentStep = firstStep;

  while (currentStep.nextStepId) {
    const nextStepId = currentStep.nextStepId;
    const nextStep = funnelSteps.find((step) => step.id === nextStepId);
    if (!nextStep) {
      break;
    }

    orderedSteps.push(nextStep);
    currentStep = nextStep;
  }

  return orderedSteps;
};

const SortableStep = ({
  step,
  stepLabel,
}: {
  step: FunnelStep;
  stepLabel: string;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: step.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="flex items-center gap-2 cursor-move"
    >
      <div className="bg-blue-100 rounded-xl p-1 text-xs min-w-10 flex items-center justify-center">
        {stepLabel}
      </div>
      <div>
        <ArrowRight className="h-4 w-4" />
      </div>
    </div>
  );
};

const FunnelCard = ({
  funnel,
  projectId,
  refetchFunnels,
}: {
  funnel: Funnel;
  projectId: string;
  refetchFunnels: () => void;
}) => {
  const [draftFunnelSteps, setDraftFunnelSteps] = useOverridableState(
    orderFunnelSteps(funnel.funnelSteps),
  );
  const { data: pages = [] } = trpc.file.list.useQuery(
    projectId ? { projectId, fileType: "page" } : skipToken,
  );

  const pagesOptions = pages?.map((page) => {
    return {
      label: getRelativePathFromFilePath(page.path),
      value: page.id,
    };
  });

  const funnelSteps = funnel.funnelSteps;

  const { mutate: addFunnelStep } = trpc.funnel.addFunnelStep.useMutation({
    onSuccess: () => {
      void refetchFunnels();
    },
  });

  const { mutate: removeFunnelStep } = trpc.funnel.removeFunnelStep.useMutation(
    {
      onSuccess: () => {
        void refetchFunnels();
      },
    },
  );

  const { mutate: updateFunnelStepOrder } =
    trpc.funnel.updateFunnelStepOrder.useMutation({
      onSuccess: () => {
        void refetchFunnels();
      },
    });

  const { mutate: deleteFunnel, isPending: deleteFunnelLoading } =
    trpc.funnel.deleteFunnel.useMutation({
      onSuccess: () => {
        void refetchFunnels();
      },
    });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleChangeFunnelSteps = (value: string[]) => {
    const stepsToAdd = value
      .filter((pageId) => !funnelSteps.some((step) => step.fileId === pageId))
      .map((pageId) => ({
        fileId: pageId,
        funnelId: funnel.id,
      }));

    if (stepsToAdd.length > 0) {
      stepsToAdd.forEach((step) => {
        addFunnelStep({
          funnelId: funnel.id,
          fileId: step.fileId,
          projectId,
        });
      });
    }

    const stepsToRemove = funnelSteps.filter(
      (step) => !value.includes(step.fileId),
    );

    if (stepsToRemove.length > 0) {
      stepsToRemove.forEach((step) => {
        removeFunnelStep({ stepId: step.id, projectId });
      });
    }
  };

  const [updatedFunnelName, setUpdatedFunnelName] = useState(funnel.name);

  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = draftFunnelSteps.findIndex(
        (step) => step.id === active.id,
      );
      const newIndex = draftFunnelSteps.findIndex(
        (step) => step.id === over.id,
      );

      const newOrder = arrayMove(draftFunnelSteps, oldIndex, newIndex);
      setDraftFunnelSteps(newOrder);

      updateFunnelStepOrder({
        projectId,
        updatedOrder: newOrder.map((step) => step.id),
      });
    }
  };

  return (
    <div className="flex flex-col justify-between p-2 border-0.5 border-gray-700 rounded-lg w-52 h-52">
      <div className="flex flex-col">
        <div>Funnel</div>
        <div>
          <Input
            value={updatedFunnelName}
            onChange={(e) => setUpdatedFunnelName(e.target.value)}
          />
        </div>
        <div className="mt-2">
          <Combobox
            size="sm"
            options={pagesOptions}
            isMultiselect
            onChange={handleChangeFunnelSteps}
            value={funnelSteps.map((step) => step.fileId)}
            trigger={
              <Combobox.SelectionButton
                size="sm"
                title="Select Pages for Funnel"
                titleAlignment="start"
                isPlaceholder={true}
              />
            }
          />
        </div>
        <div className="flex flex-wrap gap-2 mt-2 text-sm">
          {draftFunnelSteps.length === 0 && <div>No Pages in Funnel</div>}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={draftFunnelSteps.map((step) => step.id)}
              strategy={verticalListSortingStrategy}
            >
              {draftFunnelSteps.map((step) => (
                <SortableStep
                  key={step.id}
                  step={step}
                  stepLabel={getRelativePathFromFilePath(
                    pages?.find((page) => page.id === step.fileId)?.path ?? "",
                  )}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      </div>

      <div className="flex gap-2">
        <Button
          variant="danger"
          isLoading={deleteFunnelLoading}
          onClick={() => deleteFunnel({ projectId, funnelId: funnel.id })}
        >
          Delete
        </Button>

        <Link to={`/shops/${projectId}/funnels/${funnel.id}`}>
          <Button variant="primary">Open</Button>
        </Link>
      </div>
    </div>
  );
};

const PageCard = ({
  page,
  projectId,
  refetchPages,
}: {
  page: FileWithContent;
  projectId: string;
  refetchPages: () => void;
}) => {
  const { mutate: updatePagePath, isPending: updatePagePathLoading } =
    trpc.file.updatePagePath.useMutation({
      onSuccess: () => {
        void refetchPages();
      },
    });

  const { mutate: deleteFile, isPending: deleteFileLoading } =
    trpc.file.deleteFile.useMutation({
      onSuccess: () => {
        void refetchPages();
      },
    });

  const [updatedPagePath, setUpdatedPagePath] = useState(
    getRelativePathFromFilePath(page.path),
  );

  return (
    <div className="flex flex-col justify-between p-2 border-0.5 border-gray-700 rounded-lg w-48 h-48 ">
      <div className="flex flex-col">
        <div>Page</div>
        <div>
          <Input
            value={updatedPagePath}
            onChange={(e) => setUpdatedPagePath(e.target.value)}
          />
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        <Button
          variant="danger"
          isLoading={deleteFileLoading}
          onClick={() => deleteFile({ projectId, fileId: page.id })}
        >
          Delete
        </Button>
        <Button
          disabled={updatedPagePath === page.path}
          onClick={() =>
            updatePagePath({
              projectId,
              newRelativePath: updatedPagePath,
              page,
            })
          }
          isLoading={updatePagePathLoading}
          variant="primary"
        >
          Save
        </Button>
        <Link to={`/shops/${projectId}/pages/${page.id}`}>
          <Button variant="primary">Open</Button>
        </Link>
      </div>
    </div>
  );
};

export default ManageFunnels;
